import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AppRoutes } from '@/app/routes';
import { usePlayerAccess, type PlayerAccessArea } from '../controller/usePlayerAccess';

/**
 * Sends a player back to the dashboard when the coach closed this area. The menu
 * already hides it; this covers a bookmark or a typed URL. The API refuses anyway.
 */
export function PlayerAccessRoute({ area, children }: { area: PlayerAccessArea; children: ReactNode }) {
  const { t } = useTranslation('common');
  const access = usePlayerAccess();
  const denied = !access.isLoading && !access[area];

  // A bookmark that bounces back to the dashboard must say why (same toast id as the API refusal).
  useEffect(() => {
    if (denied) toast.error(t('team:playerAccess.denied'), { id: 'player-access-denied' });
  }, [denied, t]);

  if (access.isLoading) {
    // Same look as the lazy-route fallback, so a direct /stats or /video load never flashes white.
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50" role="status">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">{t('routes.loading')}</p>
        </div>
      </div>
    );
  }
  if (denied) return <Navigate to={AppRoutes.dashboard.home} replace />;
  return <>{children}</>;
}
